import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faExclamationTriangle,
  faSearch,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useAppConfig } from '../../AppConfigProvider';
import AccountModal from './AccountModal';
import './AccountManager.css';

const PayableAccountManager = ({ uId }) => {
  const appConfig = useAppConfig();
  const userID = uId;

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);


  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      console.log(`Fetching payable accounts for uId: ${userID}`);
      const response = await axios.get(
        `${appConfig.backendUrl}api/v1/qbuser/payable-accounts/${userID}`
      );
      console.log("Payable accounts response:", response.data);
      setAccounts(response.data || []);
    } finally {
      setLoading(false);
    }
  }, [appConfig.backendUrl, userID]);

  useEffect(() => {
    if (userID) {
      fetchAccounts();
    }
  }, [fetchAccounts, userID]);

  const handleCreateAccount = () => {
    setEditingAccount(null);
    setModalOpen(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setModalOpen(true);
  };

  const handleSaveAccount = async (accountName) => {
    try {
      setActionLoading(true);
      
      if (editingAccount) {
        // Update existing account
        const response = await axios.put(
          `${appConfig.backendUrl}api/v1/qbuser/payable-accounts/${editingAccount.id}`,
          {
            payableAccName: accountName,
            userId: userID
          }
        );
        
        if (response.data.success) {
          await fetchAccounts();
          setModalOpen(false);
          setEditingAccount(null);
          toast.success('Payable account updated successfully!', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          });
        } else {
          throw new Error(response.data.message || 'Failed to update account');
        }
      } else {
        // Create new account
        const response = await axios.post(
          `${appConfig.backendUrl}api/v1/qbuser/payable-accounts`,
          {
            payableAccName: accountName,
            userId: userID
          }
        );
        
        if (response.data.success) {
          await fetchAccounts();
          setModalOpen(false);
          toast.success('Payable account created successfully!', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          });
        } else {
          throw new Error(response.data.message || 'Failed to create account');
        }
      }
    } catch (error) {
      console.error('Error saving payable account:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save account';
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async (account) => {
    if (deleteConfirm?.id === account.id) {
      try {
        setActionLoading(true);
        const response = await axios.delete(
          `${appConfig.backendUrl}api/v1/qbuser/payable-accounts/${account.id}`
        );
        
        if (response.data.success) {
          await fetchAccounts();
          setDeleteConfirm(null);
          toast.success('Payable account deleted successfully!', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          });
        } else {
          throw new Error(response.data.message || 'Failed to delete account');
        }
      } catch (error) {
        console.error('Error deleting payable account:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to delete account';
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } finally {
        setActionLoading(false);
      }
    } else {
      setDeleteConfirm(account);
    }
  };

  const filteredAccounts = accounts.filter(account =>
    account.payableAccName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && accounts.length === 0) {
    return (
      <div className="account-manager">
        <div className="section-header">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faCreditCard} />
            Payable Accounts
          </h3>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading payable accounts...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="account-manager">
      <div className="section-header">
        <div>
          <h3 className="section-title">
            <FontAwesomeIcon icon={faCreditCard} />
            Payable Accounts
          </h3>
          <p className="section-subtitle">
            Manage your payable account categories ({accounts.length} total)
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={handleCreateAccount}
          disabled={actionLoading}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Account
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}

      {accounts.length > 0 && (
        <div className="search-container">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search payable accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faCreditCard} className="empty-icon" />
          <h4>No Payable Accounts</h4>
          <p>Create your first payable account to get started</p>
          <button className="btn btn-primary" onClick={handleCreateAccount}>
            <FontAwesomeIcon icon={faPlus} />
            Create First Account
          </button>
        </div>
      ) : (
        <div className="accounts-list">
          <div className="accounts-header">
            <span>Account Name</span>
            <span>Actions</span>
          </div>
          <div className="accounts-body">
            {filteredAccounts.map((account) => (
              <div key={account.id} className="account-row">
                <div className="account-row-info">
                  <h4 className="account-row-name">{account.payableAccName}</h4>
                  {account.createdAt && (
                    <p className="account-row-meta">
                      Created: {new Date(account.createdAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                <div className="account-row-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEditAccount(account)}
                    disabled={actionLoading}
                    title="Edit account"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className={`btn btn-sm ${
                      deleteConfirm?.id === account.id ? 'btn-danger-confirm' : 'btn-danger'
                    }`}
                    onClick={() => handleDeleteAccount(account)}
                    disabled={actionLoading}
                    title={deleteConfirm?.id === account.id ? 'Click again to confirm' : 'Delete account'}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    {deleteConfirm?.id === account.id && <span>Sure?</span>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchTerm && filteredAccounts.length === 0 && accounts.length > 0 && (
        <div className="no-results">
          <FontAwesomeIcon icon={faSearch} />
          <p>No accounts match your search for "{searchTerm}"</p>
        </div>
      )}

      <AccountModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingAccount(null);
        }}
        onSave={handleSaveAccount}
        account={editingAccount}
        title={editingAccount ? 'Edit Payable Account' : 'Create Payable Account'}
        accountNameLabel="Payable Account Name"
        isLoading={actionLoading}
      />
    </div>
  );
};

export default PayableAccountManager;